import { useCallback, useMemo } from "react";
import type { Artifact } from "@/artifacts/types";
import {
  tileArtifactDrill,
  tileArtifactView,
  tileArtifactZoom,
  type ArtifactSelection,
  type Tile,
} from "@/api/queries";

/**
 * A report's or dashboard's grid as one linked scope (LINK-7).
 *
 * The grid's `links` address tiles by their position in the server's list —
 * the order `sort_order` gives. The editable grid can show a dragged order
 * before the save lands, so a card's link index is looked up by tile id here
 * rather than taken from where it happens to sit on screen.
 */
export function useGridScope(
  tiles: readonly Tile[],
  { canFetch, offline = false }: { canFetch: boolean; offline?: boolean },
) {
  const ids = useMemo(
    () => [...tiles].sort((left, right) => left.sort_order - right.sort_order).map((t) => t.id),
    [tiles],
  );
  const indexOf = useCallback((tileId: string) => ids.indexOf(tileId), [ids]);
  const view = useCallback(
    (index: number, kind: string, selection: ArtifactSelection) =>
      offline
        ? unbaked(tileArtifactView(ids[index] ?? "", kind, selection).queryKey)
        : tileArtifactView(ids[index] ?? "", kind, selection),
    [ids, offline],
  );
  const drill = useCallback(
    (index: number, kind: string, selection: ArtifactSelection) =>
      tileArtifactDrill(ids[index] ?? "", kind, selection),
    [ids],
  );
  const zoom = useCallback(
    (index: number, link: string, key: string) =>
      offline
        ? unbaked(tileArtifactZoom(ids[index] ?? "", link, key).queryKey)
        : tileArtifactZoom(ids[index] ?? "", link, key),
    [ids, offline],
  );
  // A share's reader has no drill: it would run a query, and the server
  // refuses it to a share. Leaving it out keeps the table on the rows it has.
  return { indexOf, view, drill: canFetch && !offline ? drill : undefined, zoom };
}

/**
 * A page that cannot call back (LINK-6): the key the app would fetch under,
 * which the exported page seeded with what was built ahead, and a fetch that
 * never leaves the page. A selection nobody baked says so rather than asking
 * an `/api` that is not there.
 */
function unbaked(queryKey: readonly unknown[]) {
  return {
    queryKey,
    queryFn: async (): Promise<Artifact> => {
      throw new Error("Narrowing this chart for that selection needs the live app.");
    },
  };
}
